
import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../../helpers";
import { useWindowSize } from "react-use";
import { LoadingState, ProjectHeader } from "../../components";
import {
  useParams,
  useLocation,
  useNavigate,
  Routes,
  Route,
  NavLink,
} from "react-router-dom";

import VoterReachOut from "./voter-reachout-Messages/voter-reachout";
import { VoterReachOutTarget } from "./voter-reachout-Target";
import { VoterReachOutCampaigns } from "./voter-reachout-Campaigns";
import { VoterReachOutCrumbs } from "./voter-reachout-crumbs";
import { VoterReachOutTransactions } from "./voter-reachout-Campaigns/voter-reach-out-transactions";
import PageShell from "../../layouts/PageShell";
import { classNames } from "../../helpers";

import {
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  MegaphoneIcon,
  BanknotesIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";

const tabs = [
  {
    name: "Messages",
    icon: ChatBubbleLeftRightIcon,
    path: "messages",
  },
  {
    name: "Target",
    icon: UserGroupIcon,
    path: "target",
  },
  {
    name: "Campaigns",
    icon: MegaphoneIcon,
    path: "campaigns",
  },
  {
    name: "Transactions",
    icon: BanknotesIcon,
    path: "transactions",
  },
];

interface VoterReachOutIndexProps {}

const VoterReachOutIndex = observer(
  (_props: VoterReachOutIndexProps) => {
    const { width } = useWindowSize();
    const { projectid } = useParams<{ projectid: string }>();
    const location = useLocation();
    const navigate = useNavigate();

    const { voterReachOutStore } = useStore();
    const { isLoading } = voterReachOutStore;

    const isMobile = width <= 766;
    const basePath = `/project/${projectid}/voter_reach_out`;

    useEffect(() => {
      if (
        location.pathname === basePath ||
        location.pathname === `${basePath}/`
      ) {
        navigate(`${basePath}/messages`, { replace: true });
      }
    }, [location.pathname, basePath, navigate]);

    const activeTab =
      tabs.find((tab) =>
        location.pathname.startsWith(`${basePath}/${tab.path}`)
      )?.path || "messages";

    const TabView = () => (
      <div className="mb-5">
        {/* Mobile navigation */}
        <div className="sm:hidden">
          <div className="relative">
            <select
              id="voter-reachout-tabs"
              name="voter-reachout-tabs"
              aria-label="Voter Reach Out section"
              value={activeTab}
              onChange={(event) =>
                navigate(`${basePath}/${event.target.value}`)
              }
              className="h-12 w-full appearance-none rounded-lg border border-slate-200 bg-white px-4 pr-10 text-base font-medium text-slate-700 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            >
              {tabs.map((tab) => (
                <option key={tab.path} value={tab.path}>
                  {tab.name}
                </option>
              ))}
            </select>

            <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          </div>
        </div>

        {/* Desktop navigation */}
        <div className="hidden sm:block">
          <nav
            aria-label="Voter Reach Out navigation"
            className="flex flex-wrap items-center gap-1 rounded-lg border border-slate-200 bg-white p-1.5"
          >
            {tabs.map((tab) => (
              <NavLink
                key={tab.path}
                to={`${basePath}/${tab.path}`}
                className={({ isActive }) =>
                  classNames(
                    "inline-flex min-h-11 items-center justify-center gap-2 rounded-md px-4 py-2 text-base font-medium transition-colors duration-150",
                    isActive
                      ? "bg-indigo-50 text-indigo-700"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <tab.icon
                      className={classNames(
                        "h-[18px] w-[18px] shrink-0",
                        isActive ? "text-indigo-600" : "text-slate-400"
                      )}
                      aria-hidden="true"
                    />
                    <span>{tab.name}</span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
    );

    if (isLoading) {
      return (
        <>
          {!isMobile && <ProjectHeader />}

          <PageShell
            title="Voter Reach Out"
            breadcrumbs={<VoterReachOutCrumbs />}
            contentPadding="px-4"
          >
            <div className="flex min-h-[45vh] items-center justify-center">
              <LoadingState />
            </div>
          </PageShell>
        </>
      );
    }

    return (
      <>
        {!isMobile && <ProjectHeader />}

        <PageShell
          title="Voter Reach Out"
          breadcrumbs={<VoterReachOutCrumbs />}
          contentPadding="px-4"
        >
          <div className="mx-auto w-full min-w-0 max-w-[1800px] px-0 pb-6 sm:px-2">
            {/* Navigation */}
            <TabView />

            {/* Route content */}
            <div className="min-w-0">
              <Routes>
                <Route
                  path="/messages"
                  element={
                    <VoterReachOut projectid={projectid || ""} />
                  }
                />

                <Route
                  path="/target"
                  element={
                    <VoterReachOutTarget projectid={projectid} />
                  }
                />

                <Route
                  path="/campaigns"
                  element={
                    <VoterReachOutCampaigns projectid={projectid} />
                  }
                />

                <Route
                  path="/transactions"
                  element={<VoterReachOutTransactions />}
                />

                <Route
                  path="/"
                  element={
                    <VoterReachOut projectid={projectid || ""} />
                  }
                />
              </Routes>
            </div>
          </div>
        </PageShell>
      </>
    );
  }
);

export default VoterReachOutIndex;
